import type { EChartsOption } from 'echarts';
import { buildAnimationConfig } from '../shared/animation';
import { buildValueAxis, buildAxisLabelFormatter, AXIS_LABEL_FONT_SIZE } from '../shared/axis';
import { buildAgeXAxis } from './axis';
import { buildGridConfig } from '../shared/grid';
import { measureTextWidth } from '../shared/measureText';
import {
  buildTooltipConfig,
  buildLineTooltipShell,
  buildTooltipHeader,
  buildTooltipWrapper,
  buildSingleHighlightBody,
  buildMultiSeriesGridBody,
} from '../shared/tooltip';
import type { RetirementChartData, RetirementChartConfig } from './types';
import { buildRetirementSeries, RETIREMENT_SERIES_NAMES } from './series';
import { buildRetirementMarkLine } from './markLines';

export { buildZeroStateRetirementChartOption } from './zeroState';

// Matches bar/index.ts: gap between the widest tick label and the rotated axis title, plus a
// small buffer so the title clears the labels.
const AXIS_LABEL_MARGIN = 8;
const NAME_GAP_BUFFER = 6;

// Discounts each future value back to present-day buying power. Index == years from now, so the
// first point is unchanged. Mirrors the AMCharts adjustForInflation.
const adjustForInflation = (values: number[], inflationRate: number): number[] =>
  values.map((value, index) => value / Math.pow(1 + inflationRate, index));

// Resolve the three estimate arrays with inflation applied (or not). Exported so the component's
// hover-band hook computes band boundaries from the exact same values the chart renders.
export const resolveRetirementValues = (
  data: RetirementChartData,
): { high: number[]; expected: number[]; low: number[] } => {
  const adjust = (values: number[]) =>
    data.showInflationAdjusted ? adjustForInflation(values, data.inflationRate) : values;
  return {
    high: adjust(data.expectedNetInvestmentsHigh),
    expected: adjust(data.expectedNetInvestments),
    low: adjust(data.expectedNetInvestmentsLow),
  };
};

type TooltipAxisParam = {
  seriesName?: string;
  color?: unknown;
  axisValue?: number;
  value?: [number, number];
};

const buildTooltipFormatter =
  (config: RetirementChartConfig) =>
  (params: unknown): string => {
    const { theme, formatAmount, privacyMask } = config;
    const fmtVal = (val: number) => String(privacyMask(formatAmount(val, 'USD', '0,0')));

    const items = (Array.isArray(params) ? params : [params]) as TooltipAxisParam[];
    if (items.length === 0) return '';

    const age = items[0]?.axisValue ?? items[0]?.value?.[0];
    const header = buildTooltipHeader(`Age ${age ?? ''}`, theme);

    const numericItems = items.filter(
      (item): item is TooltipAxisParam & { value: [number, number] } =>
        Array.isArray(item.value) && typeof item.value[1] === 'number',
    );
    if (numericItems.length === 0) return '';

    // When the cursor is inside a specific band, show only that series (matches the line chart's
    // hovered-series tooltip); over whitespace above the bands, show all three.
    const hoveredName = config.hoveredFieldNameRef?.current ?? null;
    const visible = hoveredName ? numericItems.filter((item) => item.seriesName === hoveredName) : numericItems;
    if (visible.length === 0) return '';

    const colorOf = (item: TooltipAxisParam) =>
      typeof item.color === 'string' ? item.color : theme.textSecondaryColor;

    if (visible.length === 1) {
      const item = visible[0];
      return buildTooltipWrapper(
        header + buildSingleHighlightBody(colorOf(item), item.seriesName ?? '', fmtVal(item.value[1]), theme),
      );
    }

    const gridItems = visible.map((item) => ({
      color: colorOf(item),
      name: item.seriesName ?? '',
      formattedValue: fmtVal(item.value[1]),
    }));
    return buildTooltipWrapper(header + buildMultiSeriesGridBody(gridItems, theme));
  };

export const buildRetirementChartOption = (data: RetirementChartData, config: RetirementChartConfig): EChartsOption => {
  const {
    theme,
    animated = true,
    withLegend = true,
    formatAmount,
    privacyMask,
    title,
    yAxisTitle,
    xAxisTitle = 'Age',
  } = config;
  const { age } = data;

  const { high, expected, low } = resolveRetirementValues(data);

  const series = buildRetirementSeries(data, { high, expected, low }, config);
  // markLine belongs to a series; attach it to the top-most (highest-z) series and give the
  // markLine a high z so the vertical references paint above every area band (AMCharts:
  // range.grid.above = true).
  series[series.length - 1].markLine = buildRetirementMarkLine(data, config);

  const hasTitle = Boolean(title);
  const maxAge = age + Math.max(expected.length, 1) - 1;

  // Reserve room for the rotated y-axis title so it clears the wide "$1.50M" tick labels
  // (containLabel only measures tick labels, not the axis name). Mirrors bar/index.ts.
  const maxAbsValue = high.length ? Math.max(...high.map(Math.abs)) : 0;
  const sampleLabel = buildAxisLabelFormatter({ formatAmount, privacyMask, useNumberPrefix: true })(maxAbsValue);
  const labelWidth = measureTextWidth(sampleLabel, `${AXIS_LABEL_FONT_SIZE}px ${theme.fontFamily}`);
  const yAxisNameGap = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN + NAME_GAP_BUFFER;
  const yAxisLeft = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN;

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    ...(hasTitle
      ? {
          title: {
            text: title,
            left: 'center',
            top: 4,
            textStyle: { color: theme.textColor, fontSize: 20, fontWeight: 'bold', fontFamily: theme.fontFamily },
          },
        }
      : {}),
    legend: {
      show: withLegend,
      top: hasTitle ? 34 : 8,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 20,
      itemHeight: 10,
      textStyle: { color: theme.textColor },
      // Swatches use each series' area-fill color so the legend reads as the bands, not the
      // saturated stroke. Per-item itemStyle.color overrides the marker without touching the
      // series' own itemStyle (which colors the hover dots).
      data: [
        { name: RETIREMENT_SERIES_NAMES.high, itemStyle: { color: config.seriesColors.high.fill } },
        { name: RETIREMENT_SERIES_NAMES.expected, itemStyle: { color: config.seriesColors.expected.fill } },
        { name: RETIREMENT_SERIES_NAMES.low, itemStyle: { color: config.seriesColors.low.fill } },
      ],
    },
    // Extra top padding leaves room for the centered title + top legend.
    grid: { ...buildGridConfig({ withLegend, yAxisLeft }), top: hasTitle ? 88 : 56 },
    tooltip: {
      ...buildTooltipConfig(theme),
      ...buildLineTooltipShell(theme),
      // Lift the hover line above the opaque area bands (series z maxes at 100) so it spans the
      // full plot height instead of being covered by the fills.
      axisPointer: {
        type: 'line' as const,
        z: 200,
        lineStyle: { color: theme.todayColor, width: 1, type: 'solid' as const },
      },
      formatter: buildTooltipFormatter(config),
    },
    xAxis: buildAgeXAxis({ theme, title: xAxisTitle, min: age, max: maxAge }),
    yAxis: {
      ...buildValueAxis({
        theme,
        formatAmount,
        privacyMask,
        title: yAxisTitle,
        nameGap: yAxisNameGap,
        useNumberPrefix: true,
        showGridLines: true,
      }),
      min: 0,
    },
    series,
  };
};
